import { useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldCheck, Check, X, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Panneau admin : liste et approbation des demandes d'autorisation
 * de création de projets supplémentaires (Hub).
 */
export function HubOverrideRequestsPanel() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('pending');
    const [deciding, setDeciding] = useState(null); // { req, decision }
    const [comment, setComment] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const r = await hubAPI.listOverrideRequests(tab);
            setRequests(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [tab]); // eslint-disable-line

    const submitDecision = async () => {
        if (!deciding) return;
        try {
            await hubAPI.decideOverride(deciding.req.id, deciding.decision, comment.trim(), 1);
            toast.success(deciding.decision === 'granted' ? 'Autorisation accordée' : 'Demande refusée');
            setDeciding(null);
            setComment('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <Card data-testid="override-requests-panel">
            <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-indigo-500" /> Autorisations Projets (Hub)
                    </CardTitle>
                    <div className="flex gap-1">
                        {['pending', 'granted', 'denied'].map((s) => (
                            <Button
                                key={s}
                                size="sm"
                                variant={tab === s ? 'default' : 'outline'}
                                onClick={() => setTab(s)}
                                data-testid={`override-tab-${s}`}
                                className="text-xs"
                            >
                                {s === 'pending' ? 'En attente' : s === 'granted' ? 'Accordées' : 'Refusées'}
                            </Button>
                        ))}
                    </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Un utilisateur qui atteint son plafond de 10 projets actifs peut demander une autorisation exceptionnelle. Approuvez ou refusez ici.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-xs text-slate-400">Chargement…</div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">
                        <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        Aucune demande {tab === 'pending' ? 'en attente' : tab === 'granted' ? 'accordée' : 'refusée'}.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {requests.map((r) => (
                            <div key={r.id} className="rounded-lg border border-slate-200 p-3 space-y-1.5 bg-white" data-testid={`override-req-${r.id}`}>
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
                                            <User className="w-4 h-4" />
                                        </span>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-800">{r.user_name}</div>
                                            <div className="text-[10px] text-slate-500">{r.user_email} · {new Date(r.created_at).toLocaleString('fr-FR')}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="secondary" className="text-[10px]">
                                            {r.owned_at_request} / {r.cap_at_request} projets
                                        </Badge>
                                        {r.status === 'pending' && <Badge className="bg-amber-100 text-amber-700 text-[10px]">En attente</Badge>}
                                        {r.status === 'granted' && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Accordée (+{r.bonus_granted || 1})</Badge>}
                                        {r.status === 'denied' && <Badge className="bg-red-100 text-red-700 text-[10px]">Refusée</Badge>}
                                    </div>
                                </div>
                                <div className="rounded-md bg-slate-50 border border-slate-100 p-2 text-xs text-slate-700">
                                    <span className="font-semibold">Raison : </span>{r.reason}
                                </div>
                                {r.review_comment && (
                                    <div className="text-[11px] text-slate-500 italic">
                                        Commentaire de la décision : « {r.review_comment} »
                                    </div>
                                )}
                                {r.status === 'pending' && (
                                    <div className="flex gap-2 pt-1">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1 text-red-600 hover:text-red-700"
                                            onClick={() => { setDeciding({ req: r, decision: 'denied' }); setComment(''); }}
                                            data-testid={`override-deny-${r.id}`}
                                        >
                                            <X className="w-3.5 h-3.5" /> Refuser
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                                            onClick={() => { setDeciding({ req: r, decision: 'granted' }); setComment(''); }}
                                            data-testid={`override-grant-${r.id}`}
                                        >
                                            <Check className="w-3.5 h-3.5" /> Accorder +1 slot
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>

            <Dialog open={!!deciding} onOpenChange={(o) => { if (!o) { setDeciding(null); setComment(''); } }}>
                <DialogContent data-testid="decide-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {deciding?.decision === 'granted' ? '✅ Accorder l\'autorisation' : '❌ Refuser la demande'}
                        </DialogTitle>
                        <DialogDescription>
                            {deciding?.decision === 'granted'
                                ? `+1 slot sera accordé à ${deciding?.req?.user_name}. Ajoutez éventuellement un commentaire.`
                                : `Ajoutez un commentaire pour ${deciding?.req?.user_name} (optionnel mais recommandé).`}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        rows={3}
                        placeholder="Commentaire (optionnel)…"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        data-testid="decide-comment"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeciding(null); setComment(''); }}>Annuler</Button>
                        <Button
                            onClick={submitDecision}
                            className={deciding?.decision === 'granted' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                            data-testid="decide-submit"
                        >
                            {deciding?.decision === 'granted' ? 'Accorder' : 'Refuser'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
